import React from 'react';
import injectSheet from 'react-jss';
import cn from 'classnames';
import PropTypes from 'prop-types';
import theme, { buttonReset } from '../../styles/theme';
import round from '../../helpers/round';
import ToggleAudioButton from '../ToggleAudioButton';

const VideoControls = ({
  classes, className,
  onTogglePlayback, onFullScreenToggle, onProgressClicked,
  playing, duration, currentTime, onSpacePressed,
}) => {
  const onProgressClick = e => {
    const position = e.clientX - e.target.getBoundingClientRect().left;
    const percent = (position / e.target.getBoundingClientRect().width) * 100;
    onProgressClicked(percent);
    e.stopPropagation();
  };

  return (
    <div className={cn(classes.container, className)}>
      <ToggleAudioButton
        className={classes.audioButton}
        showText={false}
      />
      <button className={classes.button} type="button" onClick={onTogglePlayback}>
        {playing && <div className={classes.pause} />}
        {!playing && (
          <svg width="18" height="18" viewBox="0 0 90 90">
            <path fill="currentColor" d="M90 45L0 90V0z" />
          </svg>
        )}
      </button>
      <button
        className={classes.progressBarWrapper}
        onClick={onProgressClick}
        type="button"
        onKeyDown={onSpacePressed}
      >
        <div className={classes.progressBar}>
          <span className={classes.progress} style={{ width: `${duration ? round((currentTime / duration) * 100) : 0}%` }} />
        </div>
      </button>
      <button className={classes.button} type="button" onClick={onFullScreenToggle}>
        <svg width="20" height="17" viewBox="0 0 20 17">
          <g fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 5.403V1h4.247M19 10.727v4.402h-4.247M5.613 15.052H1.21v-4.248M14.387 1.078h4.403v4.247" />
          </g>
        </svg>
      </button>
    </div>
  );
};

VideoControls.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  playing: PropTypes.bool,
  onTogglePlayback: PropTypes.func,
  onFullScreenToggle: PropTypes.func,
  onProgressClicked: PropTypes.func,
  currentTime: PropTypes.number,
  duration: PropTypes.number,
  onSpacePressed: PropTypes.func,
};

VideoControls.defaultProps = {
  className: undefined,
  playing: false,
  onTogglePlayback: undefined,
  onFullScreenToggle: undefined,
  onProgressClicked: undefined,
  currentTime: 0,
  duration: undefined,
  onSpacePressed: undefined,
};

export default injectSheet({
  container: {
    display: 'flex',
    alignItems: 'center',
    margin: [0, -8],
    background: `linear-gradient(${theme.colors.transparent}, ${theme.colors.blackOpacity80})`,
    '& > *': {
      margin: [0, 8],
    },
  },
  audioButton: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    position: 'static !important',
    padding: '0 !important',
  },
  button: {
    ...buttonReset({ padding: [0, 8] }),
    cursor: 'pointer',
    flex: '0 0 auto',
    color: 'currentColor !important',
    '& > *': {
      pointerEvents: 'none',
    },
    height: 55,
    [theme.breakpoints.up('md')]: {
      height: 57,
    },
  },
  pause: {
    position: 'relative',
    width: 12,
    height: 16,
    margin: [0, 3],
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 3,
      left: 0,
      backgroundColor: 'currentColor',
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 3,
      right: 0,
      backgroundColor: 'currentColor',
    },
  },
  progressBarWrapper: {
    ...buttonReset(),
    cursor: 'pointer',
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    height: 26,
    '& > *': {
      pointerEvents: 'none',
    },
  },
  progressBar: {
    backgroundColor: theme.colors.whiteOpacity22,
    height: 2,
    display: 'flex',
    width: '100%',
  },
  progress: {
    height: 2,
    backgroundColor: theme.colors.whiteOpacity80,
    display: 'inline-block',
  },
})(VideoControls);
